<template>
  <li
    v-if="canManage"
    class="nav-item d-flex align-items-center"
  >
    <div
      v-b-tooltip.hover
      :title="hint"
      class="d-flex align-items-center border rounded four-five-frame"
      :class="frameClass"
    >
      <feather-icon
        v-if="failed"
        icon="AlertTriangleIcon"
        size="15"
        class="text-warning mr-50"
      />
      <label
        for="four-five-digit-mode"
        class="mb-0 mr-50 four-five-label"
        :class="{ 'text-muted': mode === MODE.OFF }"
      >{{ $t('fourFiveDigit.mode.label') }}</label>
      <!-- v-select แบบหน้าอื่นใน office — ป๊อปอัปเป็นของธีม ไม่ใช่ของเบราว์เซอร์ -->
      <v-select
        input-id="four-five-digit-mode"
        :value="mode"
        :options="options"
        label="text"
        :reduce="option => option.value"
        :clearable="false"
        :searchable="false"
        :disabled="loading || saving"
        placeholder="—"
        append-to-body
        :calculate-position="placeDropdown"
        class="select-size-sm four-five-select"
        @input="changeMode"
      />
    </div>
  </li>
</template>

<script>
import { VBTooltip } from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getUserData } from '@/auth/utils'
import fourFiveDigitService, { FOUR_FIVE_DIGIT_MODE, canManageFourFiveDigit } from '@/service/fourFiveDigitService'

const MODE_KEYS = {
  [FOUR_FIVE_DIGIT_MODE.OFF]: 'off',
  [FOUR_FIVE_DIGIT_MODE.LINE]: 'line',
  [FOUR_FIVE_DIGIT_MODE.ALL]: 'all',
}

// โหมด 4/5 ตัวทั้งระบบ (huay-service docs/FOUR_FIVE_DIGIT_BETS.md) · ตั้งครั้งเดียวแล้วแทบไม่แตะ
// จึงอยู่ท้ายแถบแท็บของรายการหวยแบบเดียวกับสวิตช์อัปเดตตามระบบจริง ไม่ใช่เมนูของตัวเอง
export default {
  components: {
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      MODE: FOUR_FIVE_DIGIT_MODE,
      mode: null,
      loading: true,
      saving: false,
      failed: false,
    }
  },
  computed: {
    canManage() {
      return canManageFourFiveDigit(getUserData())
    },
    // โหลดโหมดไม่ได้ก็ยังเลือกโหมดใหม่ได้ เพราะกล่องยืนยันบอกโหมดปลายทางเสมอ
    options() {
      return [FOUR_FIVE_DIGIT_MODE.OFF, FOUR_FIVE_DIGIT_MODE.LINE, FOUR_FIVE_DIGIT_MODE.ALL]
        .map(value => ({ value, text: this.modeText(value) }))
    },
    frameClass() {
      if (this.failed) return 'border-warning bg-light-warning'
      return this.mode === FOUR_FIVE_DIGIT_MODE.LINE || this.mode === FOUR_FIVE_DIGIT_MODE.ALL
        ? 'border-primary bg-light-primary' : ''
    },
    hint() {
      if (this.failed) return this.$t('fourFiveDigit.mode.loadError')
      if (this.mode === null) return ''
      return `${this.$t(`fourFiveDigit.mode.hints.${MODE_KEYS[this.mode]}`)} · ${this.$t('fourFiveDigit.mode.where')}`
    },
  },
  mounted() {
    if (this.canManage) this.load()
  },
  methods: {
    modeText(value) {
      return this.$t(`fourFiveDigit.mode.options.${MODE_KEYS[value]}`)
    },
    // ป๊อปอัปถูกย้ายไปท้าย body (append-to-body) เลยไม่ได้ระยะห่างที่ธีมให้ป๊อปอัปของ v-select และชิดกล่อง จึงเว้นเล็กน้อยเอง
    // ป๊อปอัปกว้างกว่ากล่อง (min-width ของ vue-select) และกล่องอยู่ขวาสุดของแถบแท็บ ถ้ายึดขอบซ้ายแบบเดิมจะล้นขอบจอขวา
    // จึงยึดขอบขวาให้ตรงกล่องแล้วขยายไปทางซ้ายแทน
    placeDropdown(dropdownList, component, { width, top, left }) {
      const right = document.documentElement.clientWidth - (parseFloat(left) + parseFloat(width))
      Object.assign(dropdownList.style, {
        top: `calc(${top} + 0.5rem)`, left: 'auto', right: `${right}px`, width,
      })
    },
    async load() {
      try {
        const res = await fourFiveDigitService.mode()
        if (res && res.status === 200 && res.data) {
          this.mode = Number(res.data.mode)
          this.failed = false
        } else {
          this.failed = true
        }
      } catch (error) {
        this.failed = true
      } finally {
        // ปลดล็อกไม่ว่าผลจะเป็นอย่างไร ไม่งั้นคำขอแรกที่พลาดจะทำให้เลือกโหมดไม่ได้ตลอดอายุหน้าจอ
        this.loading = false
      }
    },
    async changeMode(value) {
      if (value === this.mode) return
      // v-select แจ้งค่าก่อนปิดเมนูของตัวเอง รอให้ปิดก่อน ไม่งั้นกล่องยืนยันจำช่องของ v-select ไว้
      // แล้วคืนโฟกัสให้ตอนปิด ซึ่ง v-select จะเปิดเมนูค้างกลับขึ้นมา
      await this.$nextTick()
      // ค่าเดียวทั้งระบบและมีผลทันที จึงยืนยันก่อนทุกครั้ง · ปุ่มยังแสดงโหมดเดิมจนกว่าจะบันทึกสำเร็จ
      const result = await this.$swal({
        dark: true,
        title: this.$t('Are you sure?'),
        text: this.$t('fourFiveDigit.mode.confirm', { mode: this.modeText(value) }),
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: this.$t('Ok'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.value) return
      this.saving = true
      try {
        const res = await fourFiveDigitService.saveMode(value)
        if (res && res.status === 200) {
          this.mode = value
          this.failed = false
          this.toast('success', this.$t('บันทึกข้อมูสำเร็จ'))
        } else {
          this.toast('danger', res && typeof res.data === 'string' ? res.data : this.$t('ไม่สามารถบันทึกข้อมูลได้'))
        }
      } catch (error) {
        this.toast('danger', this.$t('ไม่สามารถบันทึกข้อมูลได้'))
      } finally {
        this.saving = false
      }
    },
    toast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: this.$t(variant === 'success' ? 'สำเร็จ' : 'เกิดข้อผิดพลาด'),
          icon: variant === 'success' ? 'CoffeeIcon' : 'AlertCircleIcon',
          variant,
          text,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

// ขนาดกรอบเท่าสวิตช์อัปเดตตามระบบจริงที่วางข้างกัน สองกล่องจึงสูงเท่ากันในแถบแท็บ
.four-five-frame {
  padding: 0.35rem 0.6rem;
}

.four-five-label {
  font-size: 0.9rem;
  line-height: 1.7rem;
  white-space: nowrap;
}

// กว้างพอให้ชื่อโหมดที่ยาวที่สุดไม่ตกบรรทัด
.four-five-select {
  min-width: 6.5rem;
}

// สูงเท่ากล่องเลือกเดิม กรอบจึงสูงเท่าสวิตช์ที่วางข้างกัน · ธีมเว้นระยะบนของชื่อที่เลือกไว้คู่กับ padding ล่างของกล่อง
// พอบีบความสูงตัวหนังสือจึงตกต่ำกว่าลูกศร จัดทุกชิ้นให้ลงกึ่งกลางแทน
// selector เจาะจงกว่าของธีม (.select-size-sm.vs--single …) เพราะ css ของ vue-select อาจโหลดทีหลังจากหน้าอื่น
.four-five-frame .four-five-select.v-select ::v-deep {
  .vs__dropdown-toggle {
    height: 1.7rem;
    padding-top: 0;
    padding-bottom: 0;
    align-items: center;
  }

  .vs__selected-options {
    align-items: center;
  }

  .vs__selected,
  .vs__search {
    margin-top: 0;
    margin-bottom: 0;
  }

  // ไม่ได้เปิดให้พิมพ์ค้นหา แต่ vue-select ยังวางช่องค้นหาโปร่งใสทับชื่อที่เลือกไว้ คลิกโดนช่องนี้ตอนเปิดอยู่มันจึงไม่ปิดป๊อปอัป
  // ให้คลิกทะลุไปถึงกล่อง คลิกตรงไหนของกล่องก็เปิด/ปิดได้ · คีย์บอร์ดยังโฟกัสช่องนี้ได้ตามเดิม
  .vs__search {
    pointer-events: none;
  }

  .vs__actions {
    padding-top: 0;
    padding-bottom: 0;
  }
}

// ตอนเปิดโหมดกรอบเป็นสีอ่อนของสีหลัก กล่องของธีมพื้นใสและเส้นขอบอ่อนมากจึงกลืนไปกับกรอบ
// ให้พื้นกล่องเป็นสีเดียวกับช่องกรอกปกติ กล่องจึงเห็นชัดทุกสีกรอบ
.four-five-frame .four-five-select.v-select ::v-deep .vs__dropdown-toggle {
  background: $input-bg;
}

.dark-layout .four-five-frame .four-five-select.v-select ::v-deep .vs__dropdown-toggle {
  background: $theme-dark-input-bg;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
